import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * TokenPoolPattern - Config-driven token accumulation with optional auto-check on gain.
 *
 * Reusable across frames:
 * - Witherwild: Wither tokens with Fear die corruption check
 * - Age of Umbra (future): Soul Blight scar-linked tracking
 *
 * Config properties:
 * - tokenKey: character state key (e.g., 'witherTokens')
 * - displayName: UI label
 * - maxTokens: pool cap
 * - icon: emoji icon
 * - colorClass: Tailwind classes for token display
 * - triggerDescription: how tokens are gained
 * - checkConfig: (optional) auto-check on token gain
 *   - checkDie: 'fear' (resolves from character.fearDie) or 'd6'/'d8' etc.
 *   - threshold: 'lessThanOrEqual' (roll <= tokens = fail)
 *   - onFail: { effect, clearTokens, description }
 * - sessionEnd: (optional) { effect, clearTokens, description }
 * - onDeath: (optional) { effect, description }
 */
const TokenPoolPattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  onClose,
}) => {
  const tokenCount = character?.[config.tokenKey] || 0;
  const maxTokens = config.maxTokens || 20;

  const [checkResult, setCheckResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);

  // ---------------------------------------------------------------------------
  // Dice helpers
  // ---------------------------------------------------------------------------

  const resolveDieType = useCallback(() => {
    const die = config.checkConfig?.checkDie;
    if (die === 'fear') return character?.fearDie || 'd6';
    return die || 'd6';
  }, [config.checkConfig, character]);

  const rollDie = useCallback((dieStr) => {
    const sides = parseInt(dieStr.replace('d', ''), 10) || 6;
    return Math.ceil(Math.random() * sides);
  }, []);

  const evaluateCheck = useCallback((roll, threshold, tokenTotal) => {
    if (threshold === 'lessThanOrEqual') return roll <= tokenTotal;
    if (threshold === 'greaterThanOrEqual') return roll >= tokenTotal;
    return false;
  }, []);

  // ---------------------------------------------------------------------------
  // Corruption / fail handler (config-driven)
  // ---------------------------------------------------------------------------

  const applyFailEffect = useCallback((newTokenCount) => {
    const failCfg = config.checkConfig?.onFail;
    if (!failCfg) return;

    const updates = { ...character };

    if (failCfg.clearTokens) {
      updates[config.tokenKey] = 0;
    }

    if (failCfg.effect === 'gainScar') {
      const newScar = {
        name: (config.displayName || 'Corruption') + ' Transformation',
        description: failCfg.description,
        source: `${frameId}-corruption`,
        gainedAt: new Date().toISOString(),
        tokenCount: newTokenCount,
      };
      updates.scars = [...(character.scars || []), newScar];
    }

    updateCharacter(updates);

    toast.error(failCfg.description, {
      duration: 5000,
      icon: config.icon || '🌿',
    });
  }, [config, character, updateCharacter, frameId]);

  // ---------------------------------------------------------------------------
  // Token actions
  // ---------------------------------------------------------------------------

  const handleAddToken = useCallback(() => {
    if (tokenCount >= maxTokens) return;

    const newCount = tokenCount + 1;

    if (config.checkConfig) {
      setIsRolling(true);

      setTimeout(() => {
        const dieType = resolveDieType();
        const roll = rollDie(dieType);
        const failed = evaluateCheck(roll, config.checkConfig.threshold, newCount);

        const result = { dieType, roll, threshold: newCount, failed };
        setCheckResult(result);
        setIsRolling(false);

        if (failed) {
          applyFailEffect(newCount);
        } else {
          updateCharacter({ ...character, [config.tokenKey]: newCount });
          toast.success(`${config.icon || '🌿'} Token gained (${newCount}/${maxTokens}) — safe!`);
        }
      }, 800);
    } else {
      updateCharacter({ ...character, [config.tokenKey]: newCount });
      toast.success(`${config.displayName || 'Token'} gained (${newCount}/${maxTokens})`);
    }
  }, [tokenCount, maxTokens, config, character, updateCharacter, resolveDieType, rollDie, evaluateCheck, applyFailEffect]);

  const handleRemoveToken = useCallback(() => {
    if (tokenCount <= 0) return;
    updateCharacter({ ...character, [config.tokenKey]: tokenCount - 1 });
  }, [tokenCount, character, config.tokenKey, updateCharacter]);

  const handleSessionEnd = useCallback(() => {
    if (tokenCount === 0) return;
    const count = tokenCount;
    updateCharacter({ ...character, [config.tokenKey]: 0 });
    toast.success(
      `Session ended. ${count} token${count !== 1 ? 's' : ''} converted to Fear.`,
      { duration: 4000, icon: '🌙' }
    );
  }, [tokenCount, character, config.tokenKey, updateCharacter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-green-400 mb-1">
          {config.icon || '🌿'} {config.displayName || 'Token Pool'}
        </h3>
        {config.triggerDescription && (
          <p className="text-sm text-gray-400">
            {config.triggerDescription}
          </p>
        )}
      </div>

      {/* Token Display */}
      <div className={`relative p-6 rounded-lg border-2 text-center ${config.colorClass || 'bg-green-900/20 border-green-600'}`}>
        <p className="text-5xl font-bold text-green-400 mb-2">
          {tokenCount}
        </p>
        <p className="text-sm text-green-300">
          / {maxTokens} tokens
        </p>

        {/* Risk indicator */}
        {config.checkConfig && tokenCount > 0 && (
          <div className="border-t border-green-600/30 pt-3 mt-3">
            <p className="text-xs text-green-400/80">
              Next check: Roll {resolveDieType().toUpperCase()}
            </p>
            <p className="text-xs text-gray-400">
              Corruption if roll &le; {tokenCount + 1}
            </p>
          </div>
        )}

        {/* Rolling overlay */}
        {isRolling && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="text-4xl animate-bounce mb-2">🎲</div>
              <p className="text-sm text-green-400 font-medium">
                Rolling Fear Die...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Token Controls */}
      <div className="flex gap-2">
        <DomainCardButton
          onClick={handleRemoveToken}
          disabled={tokenCount <= 0 || isRolling}
          variant="secondary"
          className="flex-1"
        >
          − Remove
        </DomainCardButton>
        <DomainCardButton
          onClick={handleAddToken}
          disabled={tokenCount >= maxTokens || isRolling}
          variant="warning"
          className="flex-1"
        >
          + Add Token
        </DomainCardButton>
      </div>

      {/* Session End */}
      {config.sessionEnd && (
        <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-600">
          <p className="text-sm text-gray-300 mb-2">
            {config.sessionEnd.description}
          </p>
          <DomainCardButton
            onClick={handleSessionEnd}
            variant="attack"
            className="w-full"
            disabled={tokenCount === 0}
          >
            End Session (Convert {tokenCount} to Fear)
          </DomainCardButton>
        </div>
      )}

      {/* Death Warning */}
      {config.onDeath && tokenCount > 0 && (
        <div className="bg-red-900/20 p-4 rounded-lg border border-red-600/50">
          <p className="text-xs font-bold text-red-400 mb-1">⚠️ Death Warning</p>
          <p className="text-xs text-gray-300">
            {config.onDeath.description}
          </p>
        </div>
      )}

      {/* Close */}
      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>

      {/* Check Result Modal */}
      {checkResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 cursor-pointer" onClick={() => setCheckResult(null)}>
          <div className="bg-gray-800 rounded-lg border-2 border-green-600 p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-green-400">
                {config.icon || '🌿'} Corruption Check
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Rolled {checkResult.dieType.toUpperCase()} against {checkResult.threshold} tokens
              </p>
            </div>

            <div className={`p-6 rounded-lg text-center mb-4 ${
              checkResult.failed
                ? 'bg-red-900/30 border-2 border-red-600'
                : 'bg-green-900/30 border-2 border-green-600'
            }`}>
              <div className="text-6xl font-bold mb-2">
                {checkResult.roll}
              </div>
              <div className={`text-sm ${checkResult.failed ? 'text-red-300' : 'text-green-300'}`}>
                {checkResult.roll} {checkResult.failed ? '≤' : '>'} {checkResult.threshold} — {checkResult.failed ? 'CORRUPTED' : 'SAFE'}
              </div>
            </div>

            <div className={`p-4 rounded-lg mb-4 ${
              checkResult.failed
                ? 'bg-red-900/20 border border-red-600/50'
                : 'bg-green-900/20 border border-green-600/50'
            }`}>
              <p className="text-sm text-gray-200">
                {checkResult.failed
                  ? config.checkConfig?.onFail?.description || 'Check failed.'
                  : 'You resist the corruption... for now.'}
              </p>
            </div>

            <DomainCardButton
              onClick={() => setCheckResult(null)}
              variant="secondary"
              className="w-full"
            >
              Continue
            </DomainCardButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenPoolPattern;
